import React, { useState, useEffect } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAuthStore } from '../store/authStore';
import { useToast } from '../components/ToastContainer';
import Button from '../components/Button';
import Input from '../components/Input';
import Logo from '../components/Logo';
import usePageTitle from '../utils/usePageTitle';

const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 20px;
  background-color: var(--color-background);
  position: relative;
  overflow: hidden;
`;

const LoginCard = styled.div`
  background-color: var(--color-surface);
  border-radius: var(--border-radius-lg);
  padding: 32px;
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  box-shadow: 0px 8px 24px rgba(0, 0, 0, 0.16);
  position: relative;
  z-index: 1;
`;

const LoginHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  text-align: center;
`;

const LoginTitle = styled.h1`
  font-family: var(--font-family-main);
  font-size: 30px;
  font-weight: 500;
  color: var(--color-text);
  margin: 0;
`;

const LoginSubtitle = styled.p`
  font-size: 15px;
  color: var(--color-text-secondary);
  margin: 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 8px;
`;

const PasswordRecovery = styled(Link)`
  color: var(--color-text-secondary);
  font-size: 14px;
  text-align: right;
  margin-top: -4px;
  text-decoration: none;
  transition: color 0.2s ease;
  
  &:hover {
    color: var(--color-primary);
  }
`;

const RegisterPrompt = styled.div`
  text-align: center;
  margin-top: 8px;
  font-size: 14px;
  color: var(--color-text-secondary);
  
  a {
    color: var(--color-primary);
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const EyeIcon = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--color-text-secondary);
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const BackgroundGlow = styled.div`
  position: absolute;
  width: 800px;
  height: 800px;
  border-radius: 50%;
  background-color: var(--color-primary);
  filter: blur(250px);
  opacity: 0.07;
  top: -300px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 0;
`;

export const Login: React.FC = () => {
  usePageTitle('Вход');
  
  const [formData, setFormData] = useState({
    login: '',
    password: '',
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const { login: loginFn, isAuthenticated, isLoading, isInitializing, error, clearErrors } = useAuthStore();
  const { showError } = useToast();
  const location = useLocation();
  
  // Очистка ошибок при монтировании компонента и при изменении маршрута
  useEffect(() => {
    clearErrors();
  }, [clearErrors, location.pathname]);
  
  // Показываем ошибку через toast когда она появляется
  useEffect(() => {
    if (error) {
      showError(error);
      clearErrors();
    }
  }, [error, showError, clearErrors]);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) clearErrors();
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await loginFn(formData.login, formData.password);
  };
  
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  
  const renderEyeIcon = (show: boolean, onClick: () => void) => (
    <EyeIcon onClick={onClick}>
      {show ? (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
          <circle cx="12" cy="12" r="3"></circle>
        </svg>
      ) : (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
          <line x1="1" y1="1" x2="23" y2="23"></line>
        </svg>
      )}
    </EyeIcon>
  );
  
  // Если пользователь уже авторизован (и инициализация завершена), перенаправляем на профиль
  if (isAuthenticated && !isInitializing) {
    return <Navigate to="/profile" replace />;
  }
  
  return (
    <LoginContainer>
      <BackgroundGlow />
      
      <LoginCard>
        <LoginHeader>
          <Logo size="medium" />
          <div>
            <LoginTitle>Вход</LoginTitle>
            <LoginSubtitle>Введите свои данные для входа в личный кабинет</LoginSubtitle>
          </div>
        </LoginHeader>
        
        <Form onSubmit={handleSubmit}>
          <FormField>
            <Input
              type="text"
              name="login"
              label="Введите логин"
              placeholder="Логин"
              value={formData.login}
              onChange={handleChange}
              fullWidth
              required
            />
            
            <Input
              type={showPassword ? "text" : "password"}
              name="password"
              label="Введите пароль"
              placeholder="Пароль"
              value={formData.password}
              onChange={handleChange}
              icon={renderEyeIcon(showPassword, togglePasswordVisibility)}
              iconPosition="right"
              onIconClick={togglePasswordVisibility}
              fullWidth
              required
            />
            
            <PasswordRecovery to="/recovery">
              Забыли пароль?
            </PasswordRecovery>
          </FormField>
          
          <FormActions>
            <Button 
              type="submit" 
              variant="primary" 
              fullWidth
              loading={isLoading}
            >
              Вход
            </Button>
            
            <RegisterPrompt>
              <Link to="/register">или регистрация</Link>
            </RegisterPrompt>
          </FormActions>
        </Form>
      </LoginCard>
    </LoginContainer>
  );
};

export default Login; 